import React from 'react';
import I18n from 'i18n-js';
import { ChildProps } from 'react-apollo';

import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';

import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import { conditionUuidForSlug, PGT_CSP_MODULE_CONDITIONS } from '@reverbdotcom/commons/src/condition_helpers';
import { I18N } from '@reverbdotcom/commons/src/components/translate';
import { US } from '@reverbdotcom/commons/src/constants';
import experiments from '@reverbdotcom/commons/src/experiments';
import { useViewTracking } from '@reverbdotcom/commons/src/use_tracking';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_tracker';

import PriceHistoryGraphContainer from '../shared/price_history_graph_container';
import PriceEstimates from '../shared/price_estimates';
import CSPTransactionTableContainer from '../shared/csp_transaction_table_container';
import { CSP_PRICE_REC_QUERY_DEFINITION } from '../v2_layout/queries';
import { determineEstimateFromMultipleConditions } from '../shared/helper';
import { CSP, DataServicesPriceGuideCspEstimates } from '../../../../schemas/rql';
import { formatExperimentsForEventAttributes } from '@reverbdotcom/commons/src/elog/mparticle';
import PriceGuideGraphIcon from '../../../../../images/price-guide-tool/price-guide-graphs-v2.svg';

interface IExternalProps {
  csp: CSP;
}

type IProps = ChildProps<IExternalProps, DataServicesPriceGuideCspEstimates.Query>;

export function NullState() {
  return (
    <div className="v1-csp-price-guide-module-layout__null-state__container">
      <img 
        src={PriceGuideGraphIcon} 
        alt={I18n.t('discovery.priceGuideTool.cspModule.nullState.imageAlt')} 
      />
      <div className="v1-csp-price-guide-module-layout__null-state__content">
        <div className="v1-csp-price-guide-module-layout__null-state__header">
          {I18n.t('discovery.priceGuideTool.cspModule.nullState.headerText')}
        </div>
        <div>
          {I18n.t('discovery.priceGuideTool.cspModule.nullState.explanationText')}
        </div>
      </div>
    </div>
  );
}

export function V1CSPPriceGuideModuleLayout({ 
  csp,
  data,
}: IProps) {
  const user = useUser();
  const {
    loading,
    cspPriceRecommendations = {},
  } = data;

  const estimates = cspPriceRecommendations.priceRecommendations || [];
  const sanitizedEstimates = estimates.filter(price => !!price?.priceMiddle);

  const {
    priceHigh,
    priceLow,
    priceMiddle,
    priceMiddleThirtyDaysAgo, 
  } = determineEstimateFromMultipleConditions(sanitizedEstimates);

  const hasDataServicesPriceEstimate = !!priceMiddle.amountCents;
  const conditionUuids = PGT_CSP_MODULE_CONDITIONS.map(slug => conditionUuidForSlug(slug));
  const variants = csp.variants ?? [];

  const displayNullState = variants?.length === 0 || !hasDataServicesPriceEstimate;

  useViewTracking({
    eventName: MParticleEventName.CSPPriceGuideModuleRendered,
    componentName: 'V1CSPPriceGuideModuleLayout',
    cspId: csp.id,
    cspSlug: csp.slug,
    hasDataServicesPriceEstimate,
    experiments: formatExperimentsForEventAttributes(user, [
      experiments.PRICE_GUIDE_TOOL_CSP_MODULE_V2,
    ]),
  }, !loading);

  if (loading) return null;

  return (
    <>
      {displayNullState ? (
        <NullState />
      ) : (
        <>
          <div className="v1-csp-price-guide-module-layout__estimates-container">
            <div className="v1-csp-price-guide-module-layout__estimates-container__header">
              <div className="v1-csp-price-guide-module-layout__estimates-container__header__title">
                {I18n.t('discovery.priceGuideTool.cspModule.priceEstimates.title')}
              </div>
              <I18N
                html
                text="discovery.priceGuideTool.cspModule.priceEstimates.subtitle"
              />
            </div>
            <div className="v1-csp-price-guide-module-layout__estimates-container__content">
              <PriceEstimates
                priceLow={priceLow}
                priceHigh={priceHigh}
                priceMiddle={priceMiddle}
                priceMiddleThirtyDaysAgo={priceMiddleThirtyDaysAgo}
              />
              <PriceHistoryGraphContainer
                csp={csp}
                conditionUuids={conditionUuids}
              />
            </div>
          </div>
          <div className="v1-csp-price-guide-module-layout__table-section">
            <CSPTransactionTableContainer
              csp={csp}
              conditionSlugs={PGT_CSP_MODULE_CONDITIONS}
            />
          </div>
        </>   
      )}
    </>
  );
}

const withQuery = withGraphql<IProps, DataServicesPriceGuideCspEstimates.Query>(
  CSP_PRICE_REC_QUERY_DEFINITION,
  {
    options: ({ csp }) => {
      const cspPriceRecommendationQueries = PGT_CSP_MODULE_CONDITIONS.map(condition => {
        return {
          comparisonShoppingPageId: csp.id,
          conditionUuid: conditionUuidForSlug(condition),
          countryCode: US,
        };
      });

      return {
        ssr: false,
        variables: {
          cspPriceRecommendationQueries,
        },
      };
    },
  },
);

export default withQuery(props => <V1CSPPriceGuideModuleLayout {...props} />);
